import { useEffect, useState } from "react"

import { useScreenVisible } from "@/platform/app-mode/use-screen-visible"

export const useAutoRotate = (count: number, interval: number) => {
  const [index, setIndex] = useState(0)
  const [paused, setPaused] = useState(false)
  const [stopped, setStopped] = useState(false)
  const [reducedMotion, setReducedMotion] = useState(false)
  const screenVisible = useScreenVisible()

  // Adjust-during-render (React's "adjusting state when a prop changes"
  // pattern): if the item count shrinks under the cursor, snap back to the
  // first item in the same render instead of painting an out-of-range frame.
  if (index !== 0 && index >= count) setIndex(0)
  const displayIndex = index >= count ? 0 : index

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)")
    const sync = () => setReducedMotion(query.matches)
    sync()
    query.addEventListener("change", sync)
    return () => query.removeEventListener("change", sync)
  }, [])

  useEffect(() => {
    // Don't auto-advance when the user asked for reduced motion — the crossfade
    // is disabled, so rotating would jump abruptly. They drive it via the dots.
    if (paused || stopped || reducedMotion || count < 2 || !screenVisible) return
    const id = setInterval(() => setIndex((current) => (current + 1) % count), interval)
    return () => clearInterval(id)
  }, [count, paused, stopped, interval, reducedMotion, screenVisible])

  const go = (delta: number) => {
    if (count === 0) return
    setIndex((current) => ((((current >= count ? 0 : current) + delta) % count) + count) % count)
  }
  const stop = () => setStopped(true)
  const pauseProps = {
    onMouseEnter: () => setPaused(true),
    onMouseLeave: () => setPaused(false),
    onFocus: () => setPaused(true),
    onBlur: () => setPaused(false),
  }

  return { index: displayIndex, setIndex, go, stop, pauseProps }
}
